/**
 * @fileoverview Hook for fetching groups and students data for AssignProjectModal
 */
import { useState, useEffect, useCallback } from 'react'
import { getSchoolTerms, getMyGroups, getGroupPeople } from 'pages/NewProject/service/projects'
import type { ProjectGroup, Student, GroupPeopleStudent } from './types'

interface UseAssignProjectDataParams {
  projectId: string
  opened: boolean
}

interface UseAssignProjectDataReturn {
  groups: ProjectGroup[]
  students: Student[]
  isLoading: boolean
  error: string | null
  currentSchoolTermId: string | null
  refetch: () => void
}

export const useAssignProjectData = ({
  projectId,
  opened,
}: UseAssignProjectDataParams): UseAssignProjectDataReturn => {
  const [groups, setGroups] = useState<ProjectGroup[]>([])
  const [students, setStudents] = useState<Student[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentSchoolTermId, setCurrentSchoolTermId] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!projectId) return

    setIsLoading(true)
    setError(null)

    try {
      const schoolTerms = await getSchoolTerms(projectId)
      if (!schoolTerms || schoolTerms.length === 0) {
        setError('No school terms found')
        setIsLoading(false)
        return
      }

      const currentTerm = schoolTerms.find(term => term.current) || schoolTerms[0]
      setCurrentSchoolTermId(currentTerm.value)

      const groupsData = await getMyGroups(currentTerm.value)
      if (groupsData && groupsData.length > 0) {
        const mappedGroups: ProjectGroup[] = groupsData.map(g => ({
          id: g.value,
          name: g.label,
        }))
        setGroups(mappedGroups)

        // Fetch students from ALL groups using a Map to accumulate groupIds
        const studentMap = new Map<string, Student>()

        await Promise.all(
          groupsData.map(async group => {
            const peopleData = await getGroupPeople(group.value, currentTerm.value)
            if (peopleData?.students) {
              peopleData.students.forEach((s: GroupPeopleStudent) => {
                const studentId = s.value
                if (!studentId) return

                const existing = studentMap.get(studentId)
                if (existing) {
                  // Student already seen - add this group to their list
                  existing.groupIds.push(group.value)
                } else {
                  // Backend returns: value (id), label (full name), avatar_middle
                  const nameParts = s.label?.split(' ') || []
                  studentMap.set(studentId, {
                    id: studentId,
                    firstName: nameParts[0] || '',
                    lastName: nameParts.slice(1).join(' ') || '',
                    avatar: s.avatar_middle,
                    groupIds: [group.value],
                  })
                }
              })
            }
          })
        )

        setStudents(Array.from(studentMap.values()))
      } else {
        setGroups([])
        setStudents([])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    if (opened) {
      fetchData()
    }
  }, [opened, fetchData])

  return {
    groups,
    students,
    isLoading,
    error,
    currentSchoolTermId,
    refetch: fetchData,
  }
}
